// LoyaltyService — the customer's own loyalty view, straight from the
// DinePlatform orders worker. Identity is the signed-in DineCentral account:
// the worker verifies the bearer token and only ever exposes THAT customer's
// balance, so no store credentials are involved.
//
// Online redemption is settled at PAYMENT time: reward lines in the cart carry
// item.loyaltyReward and the PPB function validates them (reward menu +
// points balance) before any money moves. In-store redemption is done by
// staff at the till. Nothing is spent from the client.

import { DINECENTRAL_AUTH } from "../Firebase.dinecentral";
import { getRemoteEnv } from "../config/remoteEnv";

const API_BASE = process.env.REACT_APP_LOYALTY_API_BASE || "https://api.dineplatform.com";

export interface LoyaltyReward {
  id: string;
  name: string;
  points: number;
  productId: string | null;
}

/** Where this store's rewards can be redeemed (set per store in the client
 *  center). Missing on older worker responses — read it via redemptionOf. */
export interface LoyaltyRedemption {
  inStore: boolean;
  online: boolean;
  /** "Redeem in store" tells the customer to scan the QR on the till's screen. */
  inStoreQr: boolean;
}

export interface LoyaltyPublicConfig {
  enabled: boolean;
  multiplier: number;
  rewards: LoyaltyReward[];
  redemption?: LoyaltyRedemption;
}

export const redemptionOf = (c: { redemption?: Partial<LoyaltyRedemption> } | null | undefined): LoyaltyRedemption => ({
  inStore: c?.redemption?.inStore !== false,
  online: c?.redemption?.online !== false,
  inStoreQr: c?.redemption?.inStoreQr !== false,
});

export interface LoyaltyStatus extends LoyaltyPublicConfig {
  email: string;
  visiblePoints: number;
  invisiblePoints: number;
  /** Total points ever earned (never reduced by spending) — the bragging number. */
  lifetimePoints?: number;
}

let publicCache: { storeId: string; value: LoyaltyPublicConfig } | null = null;

/** Unauthenticated: is loyalty on for this store, and the reward menu.
 *  Drives the Rewards tab visibility — cached per store for the session. */
export async function fetchLoyaltyPublicConfig(storeId: string): Promise<LoyaltyPublicConfig> {
  if (publicCache && publicCache.storeId === storeId) return publicCache.value;
  const appId = getRemoteEnv().appId;
  const res = await fetch(
    `${API_BASE}/loyalty/public-config?appId=${encodeURIComponent(appId)}&storeId=${encodeURIComponent(storeId)}`
  );
  if (!res.ok) throw new Error(`Loyalty config failed (${res.status})`);
  const value = (await res.json()) as LoyaltyPublicConfig;
  publicCache = { storeId, value };
  return value;
}

/** The signed-in customer's DineCentral ID token (null when signed out). */
export async function getLoyaltyToken(): Promise<string | null> {
  const user = DINECENTRAL_AUTH.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
}

/** The signed-in customer's balance + reward menu. Throws when signed out. */
export async function fetchMyLoyalty(storeId: string, testMode: boolean): Promise<LoyaltyStatus> {
  const token = await getLoyaltyToken();
  if (!token) throw new Error("Not signed in");
  const appId = getRemoteEnv().appId;
  const res = await fetch(
    `${API_BASE}/loyalty/me?appId=${encodeURIComponent(appId)}&storeId=${encodeURIComponent(storeId)}&test=${testMode === true}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const data: any = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Loyalty lookup failed (${res.status})`);
  return data as LoyaltyStatus;
}

/** Visible points already committed to reward lines in the cart. */
export const cartRewardPoints = (cart: any[]): number =>
  (cart || []).reduce((sum, it) => sum + (Number(it?.loyaltyReward?.points) || 0), 0);

export const cartHasRewards = (cart: any[]): boolean =>
  (cart || []).some((it) => it && it.loyaltyReward);
